import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { inr, formatApiErrorDetail, imgUrl, SERVICE_GST_RATES, gstPct } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Bike, Car, Ship, CarTaxiFront, Images } from "lucide-react";
import { Lightbox } from "@/components/PhotoGallery";

const TYPE_ICON = { bike: Bike, car: Car, yacht: Ship, taxi: CarTaxiFront };
const FALLBACK = "https://images.unsplash.com/photo-1502877338535-766e1452684a?w=600";

export default function Services() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [city, setCity] = useState("");
  const [type, setType] = useState("");
  const [form, setForm] = useState({ service_id: "", name: "", phone: "", email: "", message: "", when: "" });
  const [open, setOpen] = useState(false);
  const [bookForm, setBookForm] = useState({ service_id: "", name: "", phone: "", email: "", when: "", quantity: 1 });
  const [bookOpen, setBookOpen] = useState(false);
  const [booking, setBooking] = useState(false);
  const [gallery, setGallery] = useState({ open: false, images: [], title: "" });

  const load = async () => {
    const params = {};
    if (city) params.city = city;
    if (type) params.type = type;
    const { data } = await api.get("/services", { params });
    setItems(data);
  };
  useEffect(() => { load(); }, [city, type]); // eslint-disable-line react-hooks/exhaustive-deps

  const inquire = async () => {
    try {
      await api.post(`/services/${form.service_id}/inquire`, form);
      toast.success("Inquiry sent! Provider will contact you.");
      setOpen(false);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const reserve = async () => {
    setBooking(true);
    try {
      const { data } = await api.post(`/services/${bookForm.service_id}/book`, bookForm);
      setBookOpen(false);
      navigate(`/payment/${data.booking.id}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBooking(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="text-3xl sm:text-4xl font-display font-bold mb-2">Travel add-ons</h1>
      <p className="text-[#717171] mb-6">Bikes, cars, taxis & yachts — book directly from trusted local operators.</p>
      <div className="flex flex-wrap gap-2 mb-8">
        <Input data-testid="svc-city-input" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} className="rounded-xl w-40" />
        {["", "bike", "car", "taxi", "yacht"].map((t) => (
          <Button key={t || "all"} data-testid={`svc-type-${t || 'all'}`} variant={type === t ? "default" : "outline"} onClick={() => setType(t)} className="rounded-full">
            {t ? t[0].toUpperCase() + t.slice(1) : "All"}
          </Button>
        ))}
      </div>
      {items.length === 0 ? (
        <p className="text-[#717171] py-10 text-center">No services yet — providers can list at ₹199/mo (first month free).</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((s) => {
            const Icon = TYPE_ICON[s.type] || Car;
            return (
              <div key={s.id} data-testid={`service-card-${s.id}`} className="bg-white border border-[#EBEBEB] rounded-2xl overflow-hidden hover-lift">
                <button
                  type="button"
                  data-testid={`service-gallery-${s.id}`}
                  onClick={() => setGallery({ open: true, images: (s.images || []).map(imgUrl), title: s.name })}
                  className="relative w-full block group"
                >
                  <img src={s.images?.[0] ? imgUrl(s.images[0]) : FALLBACK} alt={s.name} className="w-full aspect-[4/3] object-cover group-hover:brightness-95 transition-all" />
                  {(s.images?.length || 0) > 1 && (
                    <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-white/90 text-[#222] text-xs font-semibold px-2.5 py-1 rounded-lg shadow-sm">
                      <Images className="w-3.5 h-3.5" /> {s.images.length} photos
                    </span>
                  )}
                </button>
                <div className="p-4">
                  <div className="flex items-center gap-2 text-xs text-[#FF385C] font-semibold mb-1"><Icon className="w-4 h-4" /> {s.type.toUpperCase()}</div>
                  <h3 className="font-display font-semibold">{s.name}</h3>
                  <p className="text-sm text-[#717171] mb-2">{s.city} · by {s.provider_name}</p>
                  {s.price_from > 0 && <p className="text-sm mb-3">From <strong>{inr(s.price_from)}</strong> <span className="text-xs text-[#717171]">+ {gstPct(SERVICE_GST_RATES[s.type] ?? 0.05)} GST</span></p>}
                  <div className="flex flex-wrap gap-2">
                    {user && (
                      <Dialog open={open && form.service_id === s.id} onOpenChange={(o) => !o && setOpen(false)}>
                        <DialogTrigger asChild>
                          <Button data-testid={`svc-inquire-${s.id}`} variant="outline" onClick={() => { setForm({ service_id: s.id, name: user.name, phone: "", email: user.email, message: "", when: "" }); setOpen(true); }} className="rounded-xl text-xs">Enquire</Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader><DialogTitle>Inquire about {s.name}</DialogTitle></DialogHeader>
                          <div className="space-y-3">
                            <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl" /></div>
                            <div><Label className="text-xs">When</Label><Input placeholder="e.g. Aug 5–7" value={form.when} onChange={(e) => setForm({ ...form, when: e.target.value })} className="rounded-xl" /></div>
                            <div><Label className="text-xs">Message</Label><Textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-xl" /></div>
                          </div>
                          <DialogFooter><Button onClick={inquire} className="rounded-xl bg-[#222] hover:bg-black text-white w-full">Send inquiry</Button></DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                    {user && s.price_from > 0 && (
                      <Dialog open={bookOpen && bookForm.service_id === s.id} onOpenChange={(o) => !o && setBookOpen(false)}>
                        <DialogTrigger asChild>
                          <Button data-testid={`svc-book-${s.id}`} onClick={() => { setBookForm({ service_id: s.id, name: user.name, phone: "", email: user.email, when: "", quantity: 1 }); setBookOpen(true); }} className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white text-xs">Reserve & pay</Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md" data-testid={`svc-book-dialog-${s.id}`}>
                          <DialogHeader><DialogTitle>Reserve {s.name}</DialogTitle></DialogHeader>
                          {(() => {
                            const rate = SERVICE_GST_RATES[s.type] ?? 0.05;
                            const base = (s.price_from || 0) * (Number(bookForm.quantity) || 1);
                            const gst = Math.round(base * rate);
                            const convGst = 53.82;
                            const total = base + gst + 299 + convGst;
                            return (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <div><Label className="text-xs">Quantity / days</Label><Input data-testid="svc-book-qty" type="number" min={1} value={bookForm.quantity} onChange={(e) => setBookForm({ ...bookForm, quantity: Number(e.target.value) })} className="rounded-xl" /></div>
                                  <div><Label className="text-xs">When</Label><Input data-testid="svc-book-when" placeholder="e.g. Aug 5" value={bookForm.when} onChange={(e) => setBookForm({ ...bookForm, when: e.target.value })} className="rounded-xl" /></div>
                                </div>
                                <div><Label className="text-xs">Phone</Label><Input data-testid="svc-book-phone" value={bookForm.phone} onChange={(e) => setBookForm({ ...bookForm, phone: e.target.value })} className="rounded-xl" /></div>
                                <div className="bg-[#F7F7F9] rounded-xl p-3 text-sm space-y-1">
                                  <div className="flex justify-between"><span>Service total</span><span>{inr(base)}</span></div>
                                  <div className="flex justify-between"><span>Taxes — {gstPct(rate)} GST</span><span>{inr(gst)}</span></div>
                                  <div className="flex justify-between"><span>Convenience fee</span><span>{inr(299)}</span></div>
                                  <div className="flex justify-between"><span>GST on convenience fee — 18%</span><span>{inr(convGst)}</span></div>
                                  <div className="flex justify-between font-semibold border-t border-[#EBEBEB] pt-1 mt-1"><span>Total</span><span data-testid="svc-book-total">{inr(total)}</span></div>
                                </div>
                              </div>
                            );
                          })()}
                          <DialogFooter><Button data-testid="svc-book-pay-btn" disabled={booking} onClick={reserve} className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white w-full">{booking ? "Processing..." : "Continue to payment"}</Button></DialogFooter>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Lightbox
        images={gallery.images}
        open={gallery.open}
        onClose={() => setGallery({ open: false, images: [], title: "" })}
        title={gallery.title}
        testId="service-lightbox"
      />
    </div>
  );
}
